import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { ShieldCheck, ArrowLeft } from "lucide-react";

const Section = ({ n, title, children }) => (
  <section className="mt-8">
    <h2 className="font-display text-xl font-extrabold text-[#03281B]">
      <span className="text-[#008057]">{n}.</span> {title}
    </h2>
    <div className="text-slate-600 mt-2 leading-relaxed space-y-2">{children}</div>
  </section>
);

export default function Privacy() {
  return (
    <div data-testid="privacy-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 py-12 sm:py-16">
        <Link to="/" data-testid="privacy-back-link" className="inline-flex items-center gap-2 text-sm text-[#008057] font-semibold hover:underline">
          <ArrowLeft size={16} /> Back to home
        </Link>
        <div className="mt-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#F2F8F5] text-[#008057] flex items-center justify-center shrink-0"><ShieldCheck size={24} /></div>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#03281B]">Privacy Policy</h1>
            <p className="text-sm text-slate-500 mt-1">Effective Date: June 4, 2026</p>
          </div>
        </div>

        <p className="text-slate-600 mt-6 leading-relaxed">
          Northcrest Athletics LLC ("Northcrest", "we", "our", or "us") respects your privacy and is committed to protecting your personal data in accordance with applicable privacy laws, including the Swiss Federal Act on Data Protection (FADP), the EU General Data Protection Regulation (GDPR), and applicable U.S. privacy regulations. This Privacy Policy explains how we collect, use, process, and protect personal information through our website and services.
        </p>

        <Section n="1" title="Company Information">
          <p>
            Northcrest Athletics LLC<br />
            8 The Green, Suite B<br />
            Dover, DE 19901<br />
            United States
          </p>
          <p>
            Swiss Office:<br />
            Breitfeldstrasse 54<br />
            3014 Bern<br />
            Switzerland
          </p>
          <p>
            Data Protection Contact:<br />
            Northcrest Athletics LLC — André Reis<br />
            <a href="mailto:legal@northcrestathletics.com" className="text-[#008057] font-semibold hover:underline">legal@northcrestathletics.com</a>
          </p>
        </Section>

        <Section n="2" title="Personal Data We Collect">
          <p>We may collect the following categories of personal data:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Full name</li>
            <li>Email address</li>
            <li>Phone number</li>
            <li>Date of birth</li>
            <li>Athletic information</li>
            <li>Academic information</li>
            <li>Passport or nationality information</li>
            <li>University interests</li>
            <li>Communication history</li>
            <li>Website usage data</li>
            <li>IP address and browser information</li>
          </ul>
        </Section>

        <Section n="3" title="Purpose of Data Processing">
          <p>We process personal data for the following purposes:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Providing athlete advisory services</li>
            <li>University placement support</li>
            <li>Athlete recruitment services</li>
            <li>Communication with families and athletes</li>
            <li>Website functionality and analytics</li>
            <li>Legal and compliance obligations</li>
            <li>Marketing communications (where consent applies)</li>
          </ul>
        </Section>

        <Section n="4" title="Legal Basis for Processing">
          <p>Where required under GDPR, we process personal data based on:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Consent</li>
            <li>Contractual necessity</li>
            <li>Legitimate interests</li>
            <li>Legal obligations</li>
          </ul>
        </Section>

        <Section n="5" title="Data Sharing">
          <p>We may share personal data with:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Universities and coaches</li>
            <li>Service providers</li>
            <li>Legal or regulatory authorities</li>
            <li>Technology and hosting partners</li>
          </ul>
          <p>We do not sell personal data.</p>
        </Section>

        <Section n="6" title="International Transfers">
          <p>Your information may be processed in Switzerland, the European Union, and the United States. We implement reasonable safeguards for international data transfers where required by law.</p>
        </Section>

        <Section n="7" title="Data Retention">
          <p>We retain personal information only for as long as necessary to fulfill the purposes described in this policy or to comply with legal obligations.</p>
        </Section>

        <Section n="8" title="Your Rights">
          <p>Depending on your jurisdiction, you may have rights to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Access your personal data</li>
            <li>Correct inaccurate information</li>
            <li>Request deletion</li>
            <li>Restrict processing</li>
            <li>Object to processing</li>
            <li>Withdraw consent</li>
            <li>Request data portability</li>
          </ul>
          <p>Requests may be submitted to: <a href="mailto:legal@northcrestathletics.com" className="text-[#008057] font-semibold hover:underline">legal@northcrestathletics.com</a></p>
        </Section>

        <Section n="9" title="Cookies and Analytics">
          <p>Our website may use cookies and analytics technologies to improve functionality and user experience. Please refer to our Cookie Policy for additional information.</p>
        </Section>

        <Section n="10" title="Security">
          <p>We implement commercially reasonable measures to protect personal information from unauthorized access, misuse, disclosure, or destruction.</p>
        </Section>

        <Section n="11" title="Changes">
          <p>We may update this Privacy Policy periodically. Updated versions will be published on this website.</p>
        </Section>

        <Section n="12" title="Contact">
          <p>For privacy-related questions, please contact:</p>
          <p>
            Northcrest Athletics LLC — André Reis<br />
            <a href="mailto:legal@northcrestathletics.com" className="text-[#008057] font-semibold hover:underline">legal@northcrestathletics.com</a>
          </p>
        </Section>

        <p className="text-xs text-slate-400 mt-10 pt-6 border-t border-[#E2EBF0]">Effective Date: June 4, 2026 &middot; Northcrest Athletics LLC</p>
      </div>
    </div>
  );
}
